import type { Clock } from './clock'
import { ManualClock, RealClock } from './clock'
import type { RNG } from './rng'
import { SeededRNG } from './rng'

/**
 * Test-mode contract for deterministic visual-regression runs (Playwright + pixelmatch,
 * built by a later track). Enabling test mode intercepts Math.random/performance.now/
 * Date.now globally and pins the canvas to a fixed size, so the same seed + the same
 * sequence of tick() calls always reproduces the same frames.
 */
export interface TestModeOptions {
  /** RNG seed. Defaults to 1 if omitted. */
  seed?: number
  /** Manual clock starting value in ms. Defaults to 0. */
  startMs?: number
  canvasWidth?: number
  canvasHeight?: number
}

export interface TestModeHandle {
  readonly clock: ManualClock
  readonly rng: SeededRNG
  readonly canvasWidth: number
  readonly canvasHeight: number
  /** Advance the manual clock. Does not itself step physics or render — call GameLoop.step() after. */
  tick(ms: number): void
  /** Restore the real Math.random/performance.now/Date.now. */
  disable(): void
}

export const DEFAULT_TEST_SEED = 1
export const DEFAULT_TEST_CANVAS_WIDTH = 1280
export const DEFAULT_TEST_CANVAS_HEIGHT = 720

let active: TestModeHandle | null = null

/** True if enableTestMode() has been called and not yet disabled. */
export function isTestModeEnabled(): boolean {
  return active !== null
}

/** The active test-mode handle, or null in production mode. */
export function getTestMode(): TestModeHandle | null {
  return active
}

/**
 * Enables test mode: patches global time/RNG sources and returns a handle for driving
 * the clock and reading the pinned canvas size. Idempotent - calling it again while
 * already active returns the existing handle rather than double-patching.
 */
export function enableTestMode(opts: TestModeOptions = {}): TestModeHandle {
  if (active) return active

  const clock = new ManualClock(opts.startMs ?? 0)
  const rng = new SeededRNG(opts.seed ?? DEFAULT_TEST_SEED)
  const canvasWidth = opts.canvasWidth ?? DEFAULT_TEST_CANVAS_WIDTH
  const canvasHeight = opts.canvasHeight ?? DEFAULT_TEST_CANVAS_HEIGHT

  const originalRandom = Math.random
  const originalPerformanceNow = performance.now.bind(performance)
  const originalDateNow = Date.now

  Math.random = () => rng.next()
  performance.now = () => clock.now()
  Date.now = () => clock.now()

  const handle: TestModeHandle = {
    clock,
    rng,
    canvasWidth,
    canvasHeight,
    tick(ms: number) {
      clock.advance(ms)
    },
    disable() {
      Math.random = originalRandom
      performance.now = originalPerformanceNow
      Date.now = originalDateNow
      active = null
    },
  }

  active = handle
  return handle
}

/** The clock the game loop should drive itself from: the test-mode ManualClock if active, else RealClock. */
export function resolveClock(): Clock {
  return active?.clock ?? new RealClock()
}

/** The RNG other engine/game code should pull from instead of calling Math.random() directly. */
export function resolveRng(): RNG {
  return active?.rng ?? { next: () => Math.random() }
}

/** Canvas size to render at: pinned in test mode, otherwise the real browser viewport. */
export function resolveCanvasSize(): { width: number; height: number } {
  if (active) return { width: active.canvasWidth, height: active.canvasHeight }
  return { width: window.innerWidth, height: window.innerHeight }
}
